import React, { useState, useEffect } from 'react';

const EnergyCostsWidget = ({ language = 'nl' }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  // Add this to track language changes
  const [currentLanguage, setCurrentLanguage] = useState(language);

  // Update when language prop changes
  useEffect(() => {
    setCurrentLanguage(language);
  }, [language]);

  // Translations object
  const translations = {
    nl: {
      title: "💰 Energiekosten Toelichting",
      subtitle: "Waarom betalen we meer ondanks minder verbruik?",
      contractChange: "📅 Contractwijziging:",
      contractChangeText: "Per 1 januari 2024 nieuw contract van €0,80 naar €1,35 per m³. Energiejaar loopt van juli t/m juni. Totale kosten zijn inclusief ISTA (de metertjes op de radiatoren), Kenter (meetbedrijf), Liander (energienetwerk) en jaarlijkse verhoging energiebelasting. De kosten kunnen jaarlijks veranderen, ondanks dat we een vast contract hebben, omdat Liander, ISTA en Kenter geen vaste prijzen hebben (inflatie e.d.)",
      oldContract: "2022-2023 (Oud contract)",
      transitionYear: "2023-2024 (Overgangsjaar)",
      newContract: "2024-2025 (Nieuw contract) - Verwachting",
      oldContractPeriod: "Juli 2022 - Juni 2023 • €0,80 per m³",
      transitionPeriod: "Juli 2023 - Juni 2024 • 6 mnd €0,80 + 6 mnd €1,35",
      newContractPeriod: "Juli 2024 - Juni 2025 • €1,35 per m³",
      usage: "Verbruik",
      totalCosts: "Totale kosten",
      perApartment: "Per woning",
      perCubicMeter: "Per m³ (gem.) incl. belasting",
      usagePrediction: "Verbruik (voorspelling)",
      expectedCosts: "Verwachte kosten",
      impactAnalysis: "📈 Impact Analyse",
      less: "minder",
      higher: "hoger",
      perYear: "per jaar",
      costComparison: "💡 Kostenvergelijking",
      currentUsageOldRates: "Huidig verbruik tegen oude tarieven:",
      currentUsageNewRates: "Huidig verbruik tegen nieuwe tarieven:",
      extraCostsDueToPriceIncrease: "Extra kosten door prijsstijging en belastingverhoging:",
      whyPriceIncrease: "🔍 Waarom deze prijsstijging?",
      reasons: [
        "Gestegen energieprijzen op de wereldmarkt na 2022",
        "Hogere energiebelasting van de overheid", 
        "Inflatie en gestegen transport-/distributiekosten",
        "Beëindiging van energieprijscompensatieregelingen"
      ],
      positive: "✅ Positief:",
      positiveText: "Ons verbruik daalt elk jaar! Door bewust omgaan met energie gebruiken we {percentage}% minder dan in 2022-2023. Zonder deze besparing zouden de kosten nog veel hoger zijn.",
      quickSummary: "2022-2023 → 2024-2025: 19% minder verbruikt, 45% hogere kosten",
      whatIfTitle: "💡 Wat als we niet minder waren gaan gebruiken?",
      oldUsageNewRates: "Oud verbruik (60.172 m³) tegen nieuwe tarieven:",
      actualCostsReduced: "Werkelijke kosten door minder verbruik:",
      savingsFromReduction: "✅ Besparing door minder verbruik:",
      perApartmentShort: "Per woning:"
    },
    en: {
      title: "💰 Energy Costs Explanation",
      subtitle: "Why do we pay more despite lower consumption?",
      contractChange: "📅 Contract Change:",
      contractChangeText: "New contract from January 1, 2024: from €0.80 to €1.35 per m³. Energy year runs from July to June. Total costs include ISTA (the meters on radiators), Kenter (metering company), Liander (energy network) and annual energy tax increases. Costs can change annually, despite having a fixed contract, because Liander, ISTA and Kenter do not have fixed prices (inflation, etc.)",
      oldContract: "2022-2023 (Old contract)",
      transitionYear: "2023-2024 (Transition year)",
      newContract: "2024-2025 (New contract) - Expectation",
      oldContractPeriod: "July 2022 - June 2023 • €0.80 per m³",
      transitionPeriod: "July 2023 - June 2024 • 6 months €0.80 + 6 months €1.35",
      newContractPeriod: "July 2024 - June 2025 • €1.35 per m³",
      usage: "Usage",
      totalCosts: "Total costs",
      perApartment: "Per apartment",
      perCubicMeter: "Per m³ (avg.) incl. tax",
      usagePrediction: "Usage (prediction)",
      expectedCosts: "Expected costs",
      impactAnalysis: "📈 Impact Analysis",
      less: "less",
      higher: "higher",
      perYear: "per year",
      costComparison: "💡 Cost Comparison",
      currentUsageOldRates: "Current usage at old rates:",
      currentUsageNewRates: "Current usage at new rates:",
      extraCostsDueToPriceIncrease: "Extra costs due to price increase and tax increase:",
      whyPriceIncrease: "🔍 Why this price increase?",
      reasons: [
        "Rising energy prices on the world market after 2022",
        "Higher energy taxes from the government",
        "Inflation and increased transport/distribution costs",
        "End of energy price compensation schemes"
      ],
      positive: "✅ Positive:",
      positiveText: "Our consumption is decreasing every year! Through conscious energy use, we consume {percentage}% less than in 2022-2023. Without these savings, costs would be much higher.",
      quickSummary: "2022-2023 → 2024-2025: 19% less consumption, 45% higher costs",
      whatIfTitle: "💡 What if we hadn't reduced our consumption?",
      oldUsageNewRates: "Old consumption (60,172 m³) at new rates:",
      actualCostsReduced: "Actual costs due to reduced consumption:",
      savingsFromReduction: "✅ Savings from reduced consumption:",
      perApartmentShort: "Per apartment:"
    }
  };

  // Get current translations - use currentLanguage state to ensure reactivity
  const t = translations[currentLanguage];

  // Real energy usage data from your system (July-June energy years)
  // Including ISTA and Kenter total costs
  const energyData = {
    '2022-2023': { 
      usage: 60172, 
      totalCosts: 52447, // Including ISTA and Kenter
      priceType: 'old' 
    },
    '2023-2024': { 
      usage: 53243, 
      totalCosts: 68392, // Including ISTA and Kenter
      priceType: 'mixed' 
    },
    '2024-2025': { 
      usage: 48999, // Predicted
      totalCosts: 76144, // Updated expected costs including ISTA and Kenter
      priceType: 'new' 
    }
  };

  // Calculate changes
  const usageChange = ((energyData['2024-2025'].usage - energyData['2022-2023'].usage) / energyData['2022-2023'].usage * 100);
  const costChange = ((energyData['2024-2025'].totalCosts - energyData['2022-2023'].totalCosts) / energyData['2022-2023'].totalCosts * 100);
  
  // Calculate what current usage would cost at old rates (estimate)
  const oldRate = energyData['2022-2023'].totalCosts / energyData['2022-2023'].usage;
  // eslint-disable-next-line no-unused-vars
  const currentUsageAtOldRate = Math.round(energyData['2024-2025'].usage * oldRate);
  
  // Calculate what old usage would cost at new rates
  const newRate = energyData['2024-2025'].totalCosts / energyData['2024-2025'].usage;
  const oldUsageAtNewRate = Math.round(energyData['2022-2023'].usage * newRate);
  const savingsFromReduction = oldUsageAtNewRate - energyData['2024-2025'].totalCosts;
  
  const formatNumber = (num) => new Intl.NumberFormat(currentLanguage === 'nl' ? 'nl-NL' : 'en-US').format(num);
  const formatCurrency = (amount) => new Intl.NumberFormat(currentLanguage === 'nl' ? 'nl-NL' : 'en-US', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);

  return (
    <div style={{
      background: 'linear-gradient(135deg,rgb(248, 248, 248) 0%, #e8f5e8 100%)',
      borderRadius: '12px',
      padding: '25px',
      boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
      borderLeft: '4px solid #246834',
      width: '100%',
      maxWidth: '100%',
      boxSizing: 'border-box',
      overflow: 'hidden'
    }}>
      {/* Collapsible Header */}
      <div
        onClick={() => setIsExpanded(!isExpanded)}
        style={{
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: isExpanded ? '20px' : '15px'
        }}
      >
        <div style={{ flex: 1 }}>
          <h3 style={{
            color: '#246834',
            margin: '0 0 8px 0',
            fontSize: '24px',
            fontWeight: '600'
          }}>
            {t.title}
          </h3>
          <p style={{
            color: '#555',
            margin: '0',
            fontSize: '16px',
            lineHeight: '1.5'
          }}>
            {t.subtitle}
          </p>
        </div>
        <div style={{
          fontSize: '28px',
          color: '#246834',
          transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 0.3s ease',
          flexShrink: 0,
          marginLeft: '20px'
        }}>
          ⌄
        </div>
      </div>

      {/* Quick Summary (Always Visible) */}
      {!isExpanded && (
        <div style={{
          background: '#fff3e0',
          borderRadius: '8px',
          padding: '20px',
          borderLeft: '4px solid #ff9800',
          fontSize: '16px',
          lineHeight: '1.5'
        }}>
          <div style={{
            color: '#e65100',
            fontWeight: '600',
            textAlign: 'center',
            fontSize: '18px'
          }}>
            {t.quickSummary}
          </div>
        </div>
      )}

      {/* Expanded Content */}
      {isExpanded && (
        <div>
          {/* Contract Change Notice */}
          <div style={{
            background: '#e3f2fd',
            border: '1px solid #2196f3',
            borderRadius: '8px',
            padding: '15px',
            marginBottom: '20px',
            fontSize: '15px',
            lineHeight: '1.5'
          }}>
            <strong>{t.contractChange}</strong> {t.contractChangeText}
          </div>
          
          {/* Year Comparison Cards - Full width layout */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '20px',
            marginBottom: '20px'
          }}>
            {/* 2022-2023 */}
            <div style={{
              background: 'white',
              borderRadius: '10px',
              padding: '20px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              border: '1px solid #e0e7e0'
            }}>
              <h4 style={{
                margin: '0 0 12px 0',
                fontSize: '18px',
                color: '#246834',
                fontWeight: '600'
              }}>
                {t.oldContract}
              </h4>
              <div style={{ fontSize: '14px', marginBottom: '12px', color: '#666' }}>
                {t.oldContractPeriod}
              </div>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: '1fr 1fr', 
                gap: '12px',
                fontSize: '15px'
              }}>
                <div>
                  <div style={{ color: '#666', marginBottom: '4px' }}>{t.usage}:</div>
                  <div style={{ fontWeight: '600' }}>{formatNumber(energyData['2022-2023'].usage)} m³</div>
                </div>
                <div>
                  <div style={{ color: '#666', marginBottom: '4px' }}>{t.totalCosts}:</div>
                  <div style={{ fontWeight: '600' }}>{formatCurrency(energyData['2022-2023'].totalCosts)}</div>
                </div>
                <div>
                  <div style={{ color: '#666', marginBottom: '4px' }}>{t.perApartment}:</div>
                  <div style={{ fontWeight: '600' }}>{formatCurrency(energyData['2022-2023'].totalCosts / 62)}</div>
                </div>
                <div>
                  <div style={{ color: '#666', marginBottom: '4px' }}>{t.perCubicMeter}:</div>
                  <div style={{ fontWeight: '600' }}>€{(energyData['2022-2023'].totalCosts / energyData['2022-2023'].usage).toFixed(2)}</div>
                </div>
              </div>
            </div>

            {/* 2023-2024 */}
            <div style={{
              background: 'white',
              borderRadius: '10px',
              padding: '20px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              border: '1px solid #ffa726'
            }}>
              <h4 style={{
                margin: '0 0 12px 0',
                fontSize: '18px',
                color: '#ef6c00',
                fontWeight: '600'
              }}>
                {t.transitionYear}
              </h4>
              <div style={{ fontSize: '14px', marginBottom: '12px', color: '#666' }}>
                {t.transitionPeriod}
              </div>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: '1fr 1fr', 
                gap: '12px',
                fontSize: '15px'
              }}>
                <div>
                  <div style={{ color: '#666', marginBottom: '4px' }}>{t.usage}:</div>
                  <div style={{ fontWeight: '600' }}>{formatNumber(energyData['2023-2024'].usage)} m³</div>
                </div>
                <div>
                  <div style={{ color: '#666', marginBottom: '4px' }}>{t.totalCosts}:</div>
                  <div style={{ fontWeight: '600' }}>{formatCurrency(energyData['2023-2024'].totalCosts)}</div>
                </div>
                <div>
                  <div style={{ color: '#666', marginBottom: '4px' }}>{t.perApartment}:</div>
                  <div style={{ fontWeight: '600' }}>{formatCurrency(energyData['2023-2024'].totalCosts / 62)}</div>
                </div>
                <div>
                  <div style={{ color: '#666', marginBottom: '4px' }}>{t.perCubicMeter}:</div>
                  <div style={{ fontWeight: '600' }}>€{(energyData['2023-2024'].totalCosts / energyData['2023-2024'].usage).toFixed(2)}</div>
                </div>
              </div>
            </div>

            {/* 2024-2025 */}
            <div style={{
              background: 'white',
              borderRadius: '10px',
              padding: '20px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              border: '1px solid #e0e7e0'
            }}>
              <h4 style={{
                margin: '0 0 12px 0',
                fontSize: '18px',
                color: '#246834',
                fontWeight: '600'
              }}>
                {t.newContract}
              </h4>
              <div style={{ fontSize: '14px', marginBottom: '12px', color: '#666' }}>
                {t.newContractPeriod}
              </div>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: '1fr 1fr', 
                gap: '12px',
                fontSize: '15px'
              }}>
                <div>
                  <div style={{ color: '#666', marginBottom: '4px' }}>{t.usagePrediction}:</div>
                  <div style={{ fontWeight: '600' }}>{formatNumber(energyData['2024-2025'].usage)} m³</div>
                </div>
                <div>
                  <div style={{ color: '#666', marginBottom: '4px' }}>{t.expectedCosts}:</div>
                  <div style={{ fontWeight: '600' }}>{formatCurrency(energyData['2024-2025'].totalCosts)}</div>
                </div>
                <div>
                  <div style={{ color: '#666', marginBottom: '4px' }}>{t.perApartment}:</div>
                  <div style={{ fontWeight: '600' }}>{formatCurrency(energyData['2024-2025'].totalCosts / 62)}</div>
                </div>
                <div>
                  <div style={{ color: '#666', marginBottom: '4px' }}>{t.perCubicMeter}:</div>
                  <div style={{ fontWeight: '600' }}>€{(energyData['2024-2025'].totalCosts / energyData['2024-2025'].usage).toFixed(2)}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom section with analysis and explanation - SAME WIDTH AS WHITE BOXES */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '20px',
            marginBottom: '0px'
          }}>
              {/* Impact Analysis */}
              <div style={{
                background: '#fff3e0',
                borderRadius: '8px',
                padding: '20px',
                borderLeft: '4px solid #ff9800',
                width: '100%',
                boxSizing: 'border-box'
              }}>
                <h4 style={{
                  margin: '0 0 15px 0',
                  fontSize: '18px',
                  color: '#e65100',
                  fontWeight: '600'
                }}>
                  {t.impactAnalysis}
                </h4>
                <div style={{ fontSize: '15px', marginBottom: '10px' }}>
                  <strong>{t.usage}:</strong> {Math.abs(usageChange).toFixed(1)}% {t.less} ({formatNumber(energyData['2022-2023'].usage)} → {formatNumber(energyData['2024-2025'].usage)} m³)
                </div>
                <div style={{ fontSize: '15px', marginBottom: '10px' }}>
                  <strong>{t.totalCosts}:</strong> {costChange.toFixed(1)}% {t.higher} ({formatCurrency(energyData['2022-2023'].totalCosts)} → {formatCurrency(energyData['2024-2025'].totalCosts)})
                </div>
                <div style={{ fontSize: '15px', marginBottom: '15px' }}>
                  <strong>{t.perApartment}:</strong> +{formatCurrency((energyData['2024-2025'].totalCosts - energyData['2022-2023'].totalCosts) / 62)} {t.perYear}
                </div>
                
                {/* New comparison section */}
                <div style={{
                  background: 'rgba(255, 152, 0, 0.1)',
                  padding: '15px',
                  borderRadius: '6px',
                  borderLeft: '3px solid #ff9800',
                  marginTop: '15px'
                }}>
                  <h5 style={{
                    margin: '0 0 10px 0',
                    fontSize: '16px',
                    color: '#e65100',
                    fontWeight: '600'
                  }}>
                    {t.whatIfTitle}
                  </h5>
                  <div style={{ fontSize: '14px', marginBottom: '8px' }}>
                    <strong>{t.oldUsageNewRates}</strong> {formatCurrency(oldUsageAtNewRate)}
                  </div>
                  <div style={{ fontSize: '14px', marginBottom: '8px' }}>
                    <strong>{t.actualCostsReduced}</strong> {formatCurrency(energyData['2024-2025'].totalCosts)}
                  </div>
                  <div style={{ 
                    fontSize: '15px', 
                    fontWeight: '600',
                    color: '#2e7d32',
                    borderTop: '1px solid rgba(255, 152, 0, 0.3)',
                    paddingTop: '8px'
                  }}>
                    <strong>{t.savingsFromReduction}</strong> -{formatCurrency(savingsFromReduction)}
                    <div style={{ fontSize: '13px', fontWeight: 'normal', marginTop: '4px' }}>
                      ({t.perApartmentShort} -{formatCurrency(Math.round(savingsFromReduction / 62))})
                    </div>
                  </div>
                </div>
              </div>

              {/* Explanation */}
              <div style={{
                background: '#e8f5e8',
                borderRadius: '8px',
                padding: '20px',
                borderLeft: '4px solid #246834',
                width: '100%',
                boxSizing: 'border-box'
              }}>
                <h4 style={{
                  color: '#246834',
                  margin: '0 0 15px 0',
                  fontSize: '18px',
                  fontWeight: '600'
                }}>
                  {t.whyPriceIncrease}
                </h4>
                <ul style={{
                  margin: '0 0 15px 0',
                  paddingLeft: '18px',
                  fontSize: '15px',
                  lineHeight: '1.6'
                }}>
                  {t.reasons.map((reason, index) => (
                    <li key={index} style={{ marginBottom: '8px' }}>{reason}</li>
                  ))}
                </ul>
                <div style={{
                  background: 'rgba(36, 104, 52, 0.1)',
                  padding: '15px',
                  borderRadius: '6px',
                  fontSize: '15px',
                  lineHeight: '1.6'
                }}>
                  <strong>{t.positive}</strong> {t.positiveText.replace('{percentage}', Math.abs(usageChange).toFixed(1))}
                </div>
              </div>
            </div>
        </div>
      )}
    </div>
  );
};

export default EnergyCostsWidget;